import Coupon from '@/components/Coupon/coupon';
import { couponDateTransform } from '@/components/Coupon/handler';
import Image from '@/components/Image';
import { useStore } from '@/providers/app-store-provider';
import { IS_DE } from '@/utils/constants';
import { ECouponUIType } from '@/utils/enums/coupon';
import { env } from '@/utils/env';
import Popper from '@mui/material/Popper';
import { useTranslations } from 'next-intl';

import { useEffect, useImperativeHandle, useMemo, useState } from 'react';

interface CouponListPcProps {
  couponOpened: boolean;
  anchorEl: HTMLElement;
  usableCouponList: any[];
  handleCouponApply: (item: any) => void;
  ref: any;
}

export default function CouponListPc(props: CouponListPcProps) {
  const { couponOpened, anchorEl, usableCouponList, handleCouponApply, ref } = props;

  // 能够修改isSelectedId 的状态
  useImperativeHandle(ref, () => ({
    setIsSelectedId,
  }));

  const t = useTranslations('coupons');
  const currencyList = useStore((state) => state.currencyList);
  // 将currency设置为美国,代替不翻译的场景
  const currency = currencyList.find((item) => item.name === env.NEXT_PUBLIC_DEFAULT_CURRENCY);

  const canUseCouponList = useMemo(() => {
    return usableCouponList.filter((item) => item.canuse);
  }, [usableCouponList]);

  const canNotUseCouponList = useMemo(() => {
    return usableCouponList.filter((item) => !item.canuse);
  }, [usableCouponList]);

  const handledCanUseCouponList = useMemo(() => {
    return canUseCouponList.map((item) => couponDateTransform(item, false, t, currency, IS_DE));
  }, [canUseCouponList]);

  const handledCanNotUseCouponList = useMemo(() => {
    return canNotUseCouponList.map((item) => {
      const transformed = couponDateTransform(item, false, t, currency, IS_DE);
      return {
        ...transformed,
        err_msg: item.err_msg,
      };
    });
  }, [canNotUseCouponList]);

  const [isSelectedId, setIsSelectedId] = useState<number | null>(null);

  const handleCouponClick = (item: any) => {
    handleCouponApply(item.paymentParams);
    setIsSelectedId(item.paymentParams.id);
  };

  useEffect(() => {
    if (couponOpened) {
      setIsSelectedId(null);
    }
  }, [couponOpened]);

  return (
    <div>
      <Popper
        open={couponOpened}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{
          width: anchorEl?.clientWidth,
          zIndex: 1000,
        }}
      >
        <div className=" bg-white rounded-[10px] border border-[#E0E0E0] p-[18px] max-h-[300px] overflow-y-auto">
          {usableCouponList && usableCouponList.length > 0 ? (
            <div className="flex flex-col gap-10 ">
              {handledCanUseCouponList.map((item, index) => (
                <div
                  key={`${item.id}-${index}`}
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCouponClick(item);
                  }}
                >
                  <Coupon
                    id={item.id}
                    isFirstOrder={item.isFirstOrder}
                    couponType={item.couponType}
                    couponUIType={item.couponUIType}
                    disCountText={item.disCountText}
                    title={item.title}
                    description={item.description}
                    buttonText={item.buttonText}
                    copyWriting={item.copyWriting}
                    discountTextClassName={'!text-18 !leading-18'}
                    titleClassName={'!text-14 !leading-14 pt-[4px]'}
                    containerClassName={'!px-8 !py-12 !min-h-[68px]'}
                    extension_field={item?.extension_field || {}}
                    labelPosition="top-right"
                    renderRightButton={() => (
                      <div className="flex items-center justify-center">
                        <Image
                          src={
                            isSelectedId === item.paymentParams.id
                              ? '/payment/select.svg'
                              : '/payment/not-select.svg'
                          }
                          alt="check"
                          width={16}
                          height={16}
                        />
                      </div>
                    )}
                  />
                </div>
              ))}

              {handledCanNotUseCouponList && handledCanNotUseCouponList.length > 0 && (
                <div className="text-14 ">{t('Unavailable')}</div>
              )}

              {handledCanNotUseCouponList.map((item) => (
                <div key={item.id}>
                  <Coupon
                    id={item.id}
                    isFirstOrder={item.isFirstOrder}
                    couponType={item.couponType}
                    couponUIType={ECouponUIType.DISABLED}
                    disCountText={item.disCountText}
                    title={item.title}
                    description={item.description}
                    buttonText={item.buttonText}
                    copyWriting={item.copyWriting}
                    errorMessage={item.err_msg}
                    discountTextClassName={'!text-18 !leading-18'}
                    titleClassName={'!text-14 !leading-14 pt-[4px]'}
                    containerClassName={'!px-8 !py-12 !h-[56px]'}
                    extension_field={item?.extension_field || {}}
                    labelPosition="top-right"
                    renderRightButton={() => (
                      <div className="flex items-center justify-center">
                        <div className="w-[16px] h-[16px] border-[#d5d5d5] border-1 rounded-full" />
                      </div>
                    )}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-[14px] font-bb w-full h-[60px] leading-[60px] flex items-center justify-center">
              {t('noValidCoupons')}
            </div>
          )}
        </div>
      </Popper>
    </div>
  );
}
